import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { ButtonComponent } from '@progress/kendo-angular-buttons';
import { IconComponent } from '@progress/kendo-angular-icons';
import {
  FileInfo,
  FileRestrictions,
  FileSelectComponent,
  SelectEvent,
  UploadDropZoneComponent
} from '@progress/kendo-angular-upload';

interface FileEntry {
  readonly file: File;
  readonly previewUrl: string | null;
  readonly size: string;
}

let nextZoneId = 0;

const bytesInKilobyte = 1024;
const bytesInMegabyte = bytesInKilobyte * 1024;

function formatFileSize(bytes: number): string {
  if (bytes >= bytesInMegabyte) {
    return `${+(bytes / bytesInMegabyte).toFixed(1)} MB`;
  }
  return `${Math.max(1, Math.round(bytes / bytesInKilobyte))} KB`;
}

@Component({
  selector: 'app-file-dropzone',
  templateUrl: './file-dropzone.component.html',
  styleUrl: './file-dropzone.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ButtonComponent,
    FileSelectComponent,
    IconComponent,
    UploadDropZoneComponent
  ]
})
export class FileDropzoneComponent implements ControlValueAccessor {
  private readonly destroyRef = inject(DestroyRef);
  private readonly ngControl = inject(NgControl, {
    self: true,
    optional: true
  });
  private readonly previewUrls = signal(new Map<File, string>());
  private onChange: (files: File[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  public readonly itemLabel = input('files');
  /** Lower-case extensions with a leading dot; empty means any type. */
  public readonly allowedExtensions = input<readonly string[]>([]);
  /** Maximum size of a single file in bytes; `null` means unlimited. */
  public readonly maxFileSize = input<number | null>(null);
  public readonly maxFiles = input(10);

  private readonly _value = signal<File[]>([]);
  protected readonly value = this._value.asReadonly();

  protected readonly zoneId = `app-file-dropzone-${nextZoneId++}`;
  protected readonly disabled = signal(false);
  protected readonly errors = signal<string[]>([]);
  // Kendo hides its own hover class 100 ms after the last dragover, which
  // browsers fire only every few hundred ms while the mouse is still.
  protected readonly dragOver = signal(false);

  protected readonly restrictions = computed<FileRestrictions>(() => {
    const allowedExtensions = this.allowedExtensions();
    const maxFileSize = this.maxFileSize();
    return {
      ...(allowedExtensions.length > 0
        ? { allowedExtensions: [...allowedExtensions] }
        : {}),
      ...(maxFileSize === null ? {} : { maxFileSize })
    };
  });

  protected readonly extensionList = computed(() =>
    this.allowedExtensions()
      .map(extension => extension.slice(1).toUpperCase())
      .join(', ')
  );

  protected readonly hint = computed(() => {
    const parts: string[] = [];
    const maxFileSize = this.maxFileSize();
    if (this.extensionList()) {
      parts.push(this.extensionList());
    }
    parts.push(`up to ${this.maxFiles()} files`);
    if (maxFileSize !== null) {
      parts.push(`max ${formatFileSize(maxFileSize)} each`);
    }
    return parts.join(' · ');
  });

  protected readonly entries = computed<FileEntry[]>(() => {
    const urls = this.previewUrls();
    return this.value().map(file => ({
      file,
      previewUrl: urls.get(file) ?? null,
      size: formatFileSize(file.size)
    }));
  });

  constructor() {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
    effect(() => {
      const currentFiles = new Set(this.value());
      const urls = this.previewUrls();
      let changed = false;

      // Revoke URLs for removed files
      for (const [file, url] of urls) {
        if (!currentFiles.has(file)) {
          URL.revokeObjectURL(url);
          urls.delete(file);
          changed = true;
        }
      }

      // Create URLs for new image files
      for (const file of currentFiles) {
        if (file.type.startsWith('image/') && !urls.has(file)) {
          urls.set(file, URL.createObjectURL(file));
          changed = true;
        }
      }

      if (changed) {
        this.previewUrls.set(new Map(urls));
      }
    });

    this.destroyRef.onDestroy(() => {
      this.previewUrls().forEach(url => URL.revokeObjectURL(url));
    });
  }

  writeValue(files: File[] | null): void {
    this._value.set(files ?? []);
    this.errors.set([]);
  }

  registerOnChange(fn: (files: File[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  protected browse(fileSelect: FileSelectComponent): void {
    fileSelect.fileSelectInput.nativeElement.click();
  }

  protected onSelect(event: SelectEvent): void {
    event.preventDefault();

    if (this.disabled()) {
      return;
    }

    const files = [...this.value()];
    const errors: string[] = [];
    let skippedOverLimit = 0;

    for (const info of event.files) {
      const error = this.validate(info, files);
      if (error !== null) {
        errors.push(error);
      } else if (files.length >= this.maxFiles()) {
        skippedOverLimit++;
      } else {
        files.push(info.rawFile);
      }
    }

    if (skippedOverLimit > 0) {
      errors.push(
        `Only ${this.maxFiles()} files can be attached, ${skippedOverLimit} skipped`
      );
    }
    this.errors.set(errors);
    this.commit(files);
    this.onTouched();
  }

  protected onDragLeave(event: DragEvent): void {
    const zone = event.currentTarget as HTMLElement;
    if (!zone.contains(event.relatedTarget as Node | null)) {
      this.dragOver.set(false);
    }
  }

  protected remove(file: File): void {
    this.errors.set([]);
    this.commit(this.value().filter(f => f !== file));
    this.onTouched();
  }

  private commit(files: File[]): void {
    if (files.length === this.value().length) {
      return;
    }
    this._value.set(files);
    this.onChange(files);
  }

  private validate(info: FileInfo, files: File[]): string | null {
    if (info.validationErrors?.includes('invalidFileExtension')) {
      return `${info.name}: only ${this.extensionList()} files can be attached`;
    }
    if (info.validationErrors?.includes('invalidMaxFileSize')) {
      return `${info.name}: too large (max ${formatFileSize(this.maxFileSize())})`;
    }
    if (!info.rawFile) {
      return `${info.name}: file could not be read`;
    }
    if (files.some(file => this.isSameFile(file, info.rawFile))) {
      return `${info.name}: already added`;
    }
    return null;
  }

  private isSameFile(a: File, b: File): boolean {
    return (
      a.name === b.name &&
      a.size === b.size &&
      a.lastModified === b.lastModified
    );
  }
}
